import type { PackItem } from '../../api/types';
import { fichaDoItem } from '../../data/combateTabela';
import { numero, t, umaCasa } from '../../i18n/idioma';
import { nomeDoItem } from '../../i18n/nomes';
import { ItemIcon } from '../ItemIcon';
import { stones } from '../itemUi';

/*
  A dica do item na mochila e no boneco (Renato, 04/11/2026): a mesma caixa das magias e dos efeitos
  (techspec-tooltips-e-efeitos), no lugar do `title` nativo. Os números de combate são os do perfil publicado
  (GET /api/mundo/{etag}/combate › itens, techspec-combate-centralizado); peso, durabilidade e qualidade, da peça.
*/

const pctBp = (bp: number) => umaCasa(bp / 100).replace(/,0$/, '');

/** "+10 Inteligência", "+5% vel. ataque". Zero não aparece. */
function bonusDe(b: NonNullable<ReturnType<typeof fichaDoItem>>['bonus']): string[] {
  if (!b) return [];
  const sinal = (n: number) => (n > 0 ? `+${numero(n)}` : numero(n));
  const l: string[] = [];
  if (b.str) l.push(`${sinal(b.str)} ${t('dk.at.forca')}`);
  if (b.dex) l.push(`${sinal(b.dex)} ${t('dk.at.destreza')}`);
  if (b.int) l.push(`${sinal(b.int)} ${t('dk.at.inteligencia')}`);
  if (b.velAtaque) l.push(t('dicaItem.velAtaque', { v: `${b.velAtaque > 0 ? '+' : ''}${pctBp(b.velAtaque)}` }));
  if (b.velMagia) l.push(t('dicaItem.velMagia', { v: `${b.velMagia > 0 ? '+' : ''}${pctBp(b.velMagia)}` }));
  return l;
}

export function DicaItem({ item, encaixe, nota }: {
  item: PackItem;
  /** O nome do encaixe ("Mão", "Cabeça"…), quando a peça se veste. */
  encaixe?: string | null;
  /** Uma linha de rodapé (ex.: "clique para oferecer"). */
  nota?: string;
}) {
  const f = fichaDoItem(item.defId);
  const nome = nomeDoItem(item.defId, item.name);
  const qual = item.qualidade === 2 ? { cls: ' is-exc', chave: 'oficina.excepcional' }
    : item.qualidade === 1 ? { cls: ' is-mao', chave: 'mochila.craftado' } : null;

  let destaque: string | undefined;
  let unidade: string | undefined;
  if (f?.danoMin != null && f.danoMax != null) {
    destaque = `${numero(f.danoMin)}–${numero(f.danoMax)}`;
    unidade = t('dicaItem.dano', { dado: f.dado ?? '' });
  } else if (f?.ar) {
    destaque = numero(f.ar);
    unidade = t('dicaItem.ar');
  } else if (f?.efeito != null) {
    destaque = numero(f.efeito);
    unidade = t('dicaItem.efeito');
  }

  const linhas: string[] = [];
  if (f?.velocidade != null) linhas.push(t('dicaItem.velocidade', { v: numero(f.velocidade) }));
  if (f?.alcance != null && f.alcance > 1) linhas.push(t('dicaItem.alcance', { n: numero(f.alcance) }));
  if (f?.maos === 2) linhas.push(t('dicaItem.duasMaos'));
  linhas.push(...bonusDe(f?.bonus));
  if (f?.duracaoS != null) linhas.push(t('dicaItem.duracao', { s: numero(f.duracaoS) }));

  const temDur = item.dur != null && item.durMax != null && item.durMax > 0;
  return (
    <div className="dk dk--item">
      <div className="dk__cab dk__cab--curta">
        <span className="dk__ico"><ItemIcon id={item.defId} label="" size={30} quantity={item.qty} /></span>
        <div>
          <div className="dk__nome">{nome}</div>
          {(encaixe || qual) && (
            <div className="dk__pal">
              {[encaixe, qual ? t(qual.chave) : null].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>
      </div>
      {(destaque || unidade) && <div className="dk__destaque">{destaque && <b>{destaque}</b>}{unidade && <small>{unidade}</small>}</div>}
      {linhas.length > 0 && <ul className="dk__linhas">{linhas.map(l => <li key={l}>{l}</li>)}</ul>}
      <hr />
      <div className="dk__pe">
        {item.qty > 1 && <span><b>{numero(item.qty)}</b> {t('dicaItem.unidades')}</span>}
        <span><b>{stones(item.weight)}</b> {t('comum.stones')}</span>
        {f?.forca != null && <span>{t('dicaItem.forca')} <b>{numero(f.forca)}</b></span>}
        {temDur && <span>{t('dicaItem.dur')} <b>{item.dur}/{item.durMax}</b></span>}
      </div>
      {item.feitoPor && <p className="dk__tecla">{t('oficina.feitoPor')} <b>{item.feitoPor}</b></p>}
      {nota && <p className="dk__tecla">{nota}</p>}
    </div>
  );
}
